import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Building2 } from "lucide-react";
import api from "../lib/api";

/**
 * Feb-2026 (User-Wunsch 25 May, Update v5): TMG §5 Impressum für twowheels.info.
 *
 * Pflicht-Inhalte nach deutschem Telemediengesetz (TMG §5) + DDG / RStV.
 * Vor Live-Deployment durch anwaltlich geprüfte Endfassung ersetzen und
 * konkrete Firmen-, Steuer- und Aufsichtsdaten eintragen.
 */
export default function ImpressumPage() {
    // Jul-2026 (User-Wunsch): Betreiberdaten kommen aus dem Admin-Bereich
    // (/admin/betreiber) — Fallback sind die bisher fest hinterlegten Angaben.
    const [op, setOp] = useState(null);
    useEffect(() => { api.get("/platform/operator").then((r) => setOp(r.data)).catch(() => {}); }, []);
    const o = op || {
        name: "TwoWheels", owner: "Jörg Scholz", street: "Pasinger Straße 12",
        zip: "12309", city: "Berlin", country: "Deutschland",
        phone: "+49 171 6564614", email: "info@twowheels.info", web: "www.twowheels.info",
        ust_id: "DE247256649",
    };
    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="legal-impressum-page">
            <Link to="/" onClick={(e) => { e.preventDefault(); window.history.back(); }} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#111827] mb-4">
                <ArrowLeft className="w-4 h-4" /> Zurück
            </Link>
            <div className="flex items-center gap-3 mb-6">
                <Building2 className="w-7 h-7 text-[#FFCC00]" />
                <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Impressum</h1>
            </div>
            <p className="text-sm text-gray-500 mb-8">Angaben gemäß § 5 TMG · Stand: Juni 2026</p>

            <article className="prose prose-sm max-w-none space-y-6 text-[#111827]">
                <section>
                    <h2 className="font-display text-xl font-bold mb-2">Diensteanbieter</h2>
                    <p>
                        {o.name}<br />
                        {o.owner ? <>Inhaber: {o.owner}<br /></> : null}
                        {o.street}<br />
                        {o.zip} {o.city}<br />
                        {o.country || "Deutschland"}
                    </p>
                </section>
                <section>
                    <h2 className="font-display text-xl font-bold mb-2">Vertreten durch</h2>
                    <p>Inhaber: {o.owner || o.name}</p>
                </section>
                <section>
                    <h2 className="font-display text-xl font-bold mb-2">Kontakt</h2>
                    <ul className="list-none pl-0 space-y-0.5">
                        <li>Telefon: {o.phone}</li>
                        <li>E-Mail: <a href={`mailto:${o.email}`} className="underline">{o.email}</a></li>
                        <li>Web: <a href="https://twowheels.info" className="underline">{o.web}</a></li>
                    </ul>
                </section>
                <section>
                    <h2 className="font-display text-xl font-bold mb-2">Umsatzsteuer-ID</h2>
                    <p>
                        Umsatzsteuer-Identifikationsnummer gem. § 27 a UStG:<br />
                        {o.ust_id}
                    </p>
                </section>
                <section>
                    <h2 className="font-display text-xl font-bold mb-2">Redaktionell verantwortlich</h2>
                    <p>
                        gemäß § 18 Abs. 2 MStV:<br />
                        {o.owner || o.name} (Anschrift wie oben)
                    </p>
                </section>
                <section>
                    <h2 className="font-display text-xl font-bold mb-2">EU-Streitschlichtung</h2>
                    <p>
                        Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
                        <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="underline ml-1">ec.europa.eu/consumers/odr</a>.
                        Unsere E-Mail-Adresse findest du oben.
                    </p>
                </section>
                <section>
                    <h2 className="font-display text-xl font-bold mb-2">Verbraucherstreitbeilegung / Universalschlichtungsstelle</h2>
                    <p>
                        Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
                        Verbraucherschlichtungsstelle teilzunehmen.
                    </p>
                </section>
                <section>
                    <h2 className="font-display text-xl font-bold mb-2">Haftung für Inhalte</h2>
                    <p>
                        Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach
                        den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter
                        jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen
                        oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
                    </p>
                </section>
                <section>
                    <h2 className="font-display text-xl font-bold mb-2">Haftung für Links</h2>
                    <p>
                        Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben.
                        Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der
                        verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
                    </p>
                </section>
                <section>
                    <h2 className="font-display text-xl font-bold mb-2">Urheberrecht</h2>
                    <p>
                        Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem
                        deutschen Urheberrecht. Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung
                        außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen
                        Autors bzw. Erstellers.
                    </p>
                </section>
            </article>
        </div>
    );
}
